import { trackBusinessEvent } from "./tracker";

const VIEW_GAME_DEDUPE_WINDOW_MS = 30000;
const lastViewGameMap = new Map();

function parseRequestBody(raw) {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return {};

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function normalizePath(rawUrl) {
  if (!rawUrl) return "";

  let path = String(rawUrl).trim();

  try {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      path = new URL(path).pathname;
    }
  } catch {
    // noop
  }

  const questionMark = path.indexOf("?");
  if (questionMark >= 0) {
    path = path.slice(0, questionMark);
  }

  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  if (path.startsWith("/api/")) {
    path = path.slice(4);
  } else if (path === "/api") {
    path = "/";
  }

  return path;
}

function resolveUserId(responseData) {
  return responseData?.user?._id || responseData?.user?.id || null;
}

function readCurrentUserId() {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem("userId");
  } catch {
    return null;
  }
}

function hasExtraVerification(responseData) {
  return Boolean(
    responseData?.user?.emailVerificationEnabled ??
      responseData?.user?.mfaEnabled
  );
}

function shouldTrackViewGame(gameId) {
  const now = Date.now();
  const last = lastViewGameMap.get(gameId) || 0;
  if (now - last < VIEW_GAME_DEDUPE_WINDOW_MS) return false;

  lastViewGameMap.set(gameId, now);

  if (lastViewGameMap.size > 250) {
    const expiration = now - VIEW_GAME_DEDUPE_WINDOW_MS;
    for (const [id, timestamp] of lastViewGameMap.entries()) {
      if (timestamp < expiration) lastViewGameMap.delete(id);
    }
  }

  return true;
}

export function trackBusinessEventFromAxiosResponse(response) {
  if (!response || !response.config) return;

  const method = String(response.config.method || "GET").toUpperCase();
  const path = normalizePath(response.config.url);
  const requestBody = parseRequestBody(response.config.data);
  const responseData = response.data || {};

  if (method === "POST" && path === "/auth/register") {
    const userId = resolveUserId(responseData);
    trackBusinessEvent(
      "signup",
      {
        auth_method: "password",
        result: "success",
        user_id: userId || null,
        has_mfa: hasExtraVerification(responseData),
      },
      { source: "api.auth.register", userId }
    );
    return;
  }

  if (method === "POST" && path === "/auth/login") {
    if (Boolean(responseData?.mfaRequired)) return;
    const userId = resolveUserId(responseData);

    trackBusinessEvent(
      "login",
      {
        auth_method: "password",
        result: "success",
        user_id: userId || null,
        has_mfa: hasExtraVerification(responseData),
      },
      { source: "api.auth.login", userId }
    );
    return;
  }

  if (method === "POST" && path === "/auth/mfa/verify-login") {
    const userId = resolveUserId(responseData);

    trackBusinessEvent(
      "login",
      {
        auth_method: "password_email_code",
        result: "success",
        user_id: userId || null,
        has_mfa: true,
      },
      { source: "api.auth.mfa.verify", userId }
    );
    return;
  }

  if (method === "PATCH" && /^\/friends\/accept\/[^/]+$/.test(path)) {
    const targetUserId = path.split("/").pop() || null;
    const currentUserId = readCurrentUserId();

    trackBusinessEvent(
      "add_friend",
      {
        user_id: currentUserId,
        target_user_id: targetUserId,
        source_flow: "accept_request",
      },
      { source: "api.friends.accept", userId: currentUserId }
    );
    return;
  }

  if (method === "POST" && path === "/messages/send") {
    const currentUserId = readCurrentUserId();
    const text = String(requestBody?.text || "").trim();

    trackBusinessEvent(
      "send_message",
      {
        user_id: currentUserId,
        target_user_id: requestBody?.to ? String(requestBody.to) : null,
        channel: "direct",
        char_count: text.length,
      },
      { source: "api.messages.send", userId: currentUserId }
    );
    return;
  }

  const gameMatch = path.match(/^\/games\/(\d+)$/);
  if (method === "GET" && gameMatch) {
    const gameId = Number(gameMatch[1]);
    if (!Number.isFinite(gameId) || !shouldTrackViewGame(gameId)) return;

    trackBusinessEvent(
      "view_game",
      {
        game_id: gameId,
        game_name: responseData?.data?.name || responseData?.name || null,
        source_flow: "api_game_detail",
        user_id: readCurrentUserId(),
      },
      { source: "api.games.detail", userId: readCurrentUserId() }
    );
    return;
  }

  if (method === "POST" && path === "/reviews") {
    const currentUserId = readCurrentUserId();
    const ratingStars = Number(requestBody?.ratingStars);

    trackBusinessEvent(
      "add_review",
      {
        user_id: currentUserId,
        game_id: Number(requestBody?.gameId) || null,
        rating_stars: Number.isFinite(ratingStars) ? ratingStars : null,
        has_text: Boolean(String(requestBody?.reviewText || "").trim()),
        contains_spoilers: Boolean(requestBody?.containsSpoilers),
      },
      { source: "api.reviews.create", userId: currentUserId }
    );
  }
}
